import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subscription, interval, of } from "rxjs";
import { catchError, startWith, switchMap } from "rxjs/operators";
import { PaymentService } from "../../../@core/backend/receptionist/services/payment-service";
import { SignUpService } from "../../../@core/backend/receptionist/services/sign-up-service";
import { TopUpConfirmationStatus, TopUpIntentType } from "../../../@core/interfaces/common/payment-enums";

@Component({
  selector: "r247-registration-confirmation",
  templateUrl: "./registration-confirmation.component.html",
})
export class R247RegistrationConfirmationComponent implements OnInit, OnDestroy {
  PROCESSING = "processing";
  SUCCESS = "success";
  FAIL = "fail";

  state = this.PROCESSING;
  errorMessage = "";

  private pollSubscription?: Subscription;
  private intentId = "";
  private intentType: string = TopUpIntentType.None;
  private clientId = 0;

  constructor(
    private route: ActivatedRoute,
    private paymentService: PaymentService,
    private signUpService: SignUpService
  ) {}

  ngOnInit(): void {
    this.intentId = this.route.snapshot.queryParamMap.get("intentId") || "";
    this.intentType = this.route.snapshot.queryParamMap.get("intentType");
    this.clientId = Number(this.route.snapshot.queryParamMap.get("clientId") || 0);

    if (!this.intentId || this.intentType === TopUpIntentType.None) {
      this.state = this.FAIL;
      this.errorMessage = "An error occurred trying to confirm your top up.";
      return;
    }

    if (!this.clientId || this.clientId <= 0) {
      this.state = this.FAIL;
      this.errorMessage = "An error occurred trying to activate your reception.";
      return;
    }

    this.pollSubscription = interval(2000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.getStatus().pipe(
            catchError(() => of(null))
          )
        )
      )
      .subscribe((statusResponse) => {
        if (statusResponse.status == TopUpConfirmationStatus.pending)
          return;

        this.pollSubscription?.unsubscribe();

        if (statusResponse.status == TopUpConfirmationStatus.failed)
        {
          this.state = this.FAIL;
          this.errorMessage = this.getErrorMessage(statusResponse);

          return;
        }

        this.signUpService.activate(this.clientId).subscribe(
          (activateResponse) => {
            if ((activateResponse?.status || "").toLowerCase() === "success") {
              this.state = this.SUCCESS;
              return;
            }

            this.state = this.FAIL;
            this.errorMessage = activateResponse?.errorMessage || "Unable to activate reception.";
          },
          (error) => {
            this.state = this.FAIL;
            this.errorMessage = error?.error?.errorMessage || "Unable to activate reception.";
          }
        );
      });
  }

  ngOnDestroy(): void {
    this.pollSubscription?.unsubscribe();
  }

  private getStatus() {
    return this.paymentService.getIntentStatus(this.intentId);
  }

  private isCompleted(response: any): boolean {
    if (typeof response === "boolean")
      return response;

    if (typeof response === "number")
      return response === 1;

    if (typeof response === "string") {
      const normalised = response.toLowerCase();
      return normalised === "1" || normalised === "completed" || normalised === "complete" || normalised === "true";
    }

    const state = response?.status ?? response?.state;
    if (typeof state === "number")
      return state === 1;

    if (typeof state === "string") {
      const normalised = state.toLowerCase();
      if (normalised === "1" || normalised === "completed" || normalised === "complete" || normalised === "true")
        return true;
    }

    if (typeof response?.completed === "boolean")
      return response.completed;

    if (typeof response?.isCompleted === "boolean")
      return response.isCompleted;

    return false;
  }

  private getOutcome(response: any): boolean | null {
    if (typeof response?.success === "boolean")
      return response.success;

    if (typeof response?.isSuccess === "boolean")
      return response.isSuccess;

    const redirectStatus = (this.route.snapshot.queryParamMap.get("redirect_status") || "").toLowerCase();

    if (redirectStatus === "succeeded")
      return true;

    if (redirectStatus === "failed")
      return false;

    return null;
  }

  private getErrorMessage(response: any): string {
    if (response?.errorMessage)
      return response.errorMessage;

    return "We were unable to confirm your payment.";
  }
}
